// react
import React, { useRef, useEffect } from 'react';
import ClockIcon from '../icons/Clock.svg';

// utils
import { formatTimestamp, formatDateStamp, areTimestampsEqual } from "../utils/timestampFormatter";
import { smoothScrollTo, easeInOutSeptic } from '../utils/helpers';
import { TIMEZONES } from '../utils/consts';
import * as translator from '../utils/translator';

// types
import { DateAvailability } from 'data-types';

// components
import Skeletonize from '../Skeletons/Skeletonize';


type Props = {
	isShowing?: boolean;
	selectedDateSlots: DateAvailability | null;
	onTimeSelection: (timeSlot: {start: string, end: string}) => void;
	selectedSlot: string | null;
	additionalClassName?: string;
	language?: string;
	country?: string;
	isLoading?: boolean;
	mobileScrollTargetRef?: React.RefObject<HTMLElement>;
	scrollDelay?: number;
	animateSelected?: boolean;
	scrollOffsetAdjustment?: number;
}

const DoctorAvailableSlots: React.FC<Props> = ({ 
	isShowing=true, 
	selectedDateSlots, 
	onTimeSelection, 
	selectedSlot,
	additionalClassName,
	language="de",
	country="de",
	isLoading=false, 
	mobileScrollTargetRef, 
	scrollDelay=0, 
	scrollOffsetAdjustment=0,
	animateSelected=false
}) => {
	const containerRef = useRef<HTMLDivElement>(null);

	// Helper function to create safe HTML ID from timestamp
	const createSlotId = (timestamp: string) => {
		return `doctor-available-time-slot-${timestamp.replace(/[^a-zA-Z0-9]/g, '-')}`;
	};

	const scrollToSelectedSlot = (selectedSlot: string | null, deley: number = 0) => {
		// Determine which container to scroll based on screen size
		
		const isMobile = window.innerWidth <= 768;
		const scrollContainer = isMobile && mobileScrollTargetRef?.current 
		? mobileScrollTargetRef.current
		: containerRef.current;
		
		if (!scrollContainer) return;
		
		// If no slot is selected, scroll to the top
		if (!selectedSlot) {
			if (!isMobile) {
				smoothScrollTo(scrollContainer, 0, 0);
			}
			return;
		}
		
		const desiredOffset = 10 + scrollOffsetAdjustment;
		const slotId = createSlotId(selectedSlot);
		const slotElement = document.getElementById(slotId);
		
		if (!slotElement) return;

		const container = scrollContainer;
		const containerRect = container.getBoundingClientRect();
		const slotRect = slotElement.getBoundingClientRect();
		const currentScrollTop = container.scrollTop;

		// calculate desired movement from top
		const containerTopEdge = containerRect.top;
		const slotTopEdge = slotRect.top;
		const desiredMovementFromTop = Math.max(0, Math.round(containerTopEdge - (slotTopEdge - desiredOffset)));


		// calculate desired movement from bottom
		const containerBottomEdge =  containerTopEdge + containerRect.height;
		const slotBottomEdge = slotRect.bottom;
		const desiredMovementFromBottom = Math.max(0, Math.round((slotBottomEdge + desiredOffset) - containerBottomEdge));


		// pick the the movement which will make the slot completely visible
		const absDesiredScrollFromTop = desiredMovementFromTop > desiredMovementFromBottom 
		? currentScrollTop - desiredMovementFromTop
		: currentScrollTop + desiredMovementFromBottom;


		let timeoutId: NodeJS.Timeout | null = null;

		if (Math.abs(desiredMovementFromTop - desiredMovementFromBottom) > 2) {
			// Small delay to ensure DOM is updated
			timeoutId = setTimeout(() => {
				// Smooth scroll to the calculated position
				smoothScrollTo(container, absDesiredScrollFromTop, 1500, easeInOutSeptic);
			}, deley);
		}
		
		return timeoutId;
	}


	const handleTimeSelectionWithScroll = (timeSlot: {start: string, end: string}) => {
		onTimeSelection(timeSlot);
		scrollToSelectedSlot(timeSlot.start, 100);
	}

	
	// Effect to scroll to selected slot when it changes
	useEffect(() => {
		if (isShowing && !isLoading) {
			const scrollToSelectedSlotTimeoutId = scrollToSelectedSlot(selectedSlot, scrollDelay);
			return () => clearTimeout(scrollToSelectedSlotTimeoutId as NodeJS.Timeout);
		}
	}, [isShowing, selectedDateSlots]);

	// Factory function for skeleton time slots
	const timeSlotComponentFactory = (key: React.Key, isLoading: boolean) => (
		<DoctorAvailableTimeSlot 
			key={key}
			timeSlot={{ start: '', end: '' }} // placeholder data
			onClick={() => {}} // no-op for skeleton
			isSelected={false}
			slotId={`skeleton-${key}`}
			isLoading={isLoading}
			animateSelected={animateSelected}
		/>
	);

	return (
		<div 
			ref={containerRef}
			id="doctor-available-time-slots-container"
			className={`
        	    rounded-[16px]
				bg-[#ffffff]
				px-5 lg:px-8
				py-4 lg:py-9
				h-fit md:h-auto
				custom-scrollbar
				
				${additionalClassName || ''}
			`}
		>
			<Skeletonize
				isLoading={isLoading}
				className='h-[24px] md:h-[26px] mb-[11px] md:mb-[19px]'
				animation='syncWave pulse'
			>
				<h3
					id="doctor-available-slots-title"
					className="
						leading-[1.3]
						text-[21px] lg:text-[23px]  
						font-medium
						text-[#000000]
						first-letter:uppercase
						pb-[1em]
						animate-fade-in-up-10
					"
				>
					{translator.translate(language, "default", "schedule_video_consultation/available_times")}
				</h3>
			</Skeletonize>

			<Skeletonize
				isLoading={isLoading}
				className='h-[15px] mb-[30px] md:mb-[35px]'
				animation='syncWave pulse'
			>
				<h4
					id="doctor-available-slots-subtitle"
					className="
						font-normal
						first-letter:uppercase
						font-roboto
						text-[#4B5B4B]
						text-[13px] md:text-[14px] lg:text-[15px]
						pb-[2.5em] md:pb-[1.2em]
						animate-fade-in-up-10
					"
				>
					{selectedDateSlots?.date 
					 	? 
							`${translator.translate(language, "default", "schedule_video_consultation/available_slots_for")} ${formatDateStamp(selectedDateSlots.date, { showDay: false, showYear: false, locale: `${language}-${country.toUpperCase()}`, timeZone: TIMEZONES[country as keyof typeof TIMEZONES] })}` 
						: 
							translator.translate(language, "default", "schedule_video_consultation/no_date_selected")}
				</h4>
			</Skeletonize>

			<ul 
				id="doctor-available-times-list"
				className="
					grid
					grid-cols-2
					auto-rows-max
					gap-4
				"
			>
				<Skeletonize
					isLoading={isLoading}
					skeletonCount={12}
					componentFactory={timeSlotComponentFactory}
				>
					{({ disableSkeleton, isLoading }) =>
						selectedDateSlots?.timeslots?.map((doctorAvailabilityData) => (
							<DoctorAvailableTimeSlot 
								key={doctorAvailabilityData.start}
								timeSlot={doctorAvailabilityData} 
								onClick={handleTimeSelectionWithScroll}
								isSelected={areTimestampsEqual(selectedSlot, doctorAvailabilityData.start)}
								slotId={createSlotId(doctorAvailabilityData.start)}
								isLoading={isLoading}
								disableSkeleton={disableSkeleton}
								animateSelected={animateSelected}
							/>
						))
					}
				</Skeletonize>
			</ul>
		</div>
	);
}

export default DoctorAvailableSlots;


type DoctorAvailableTimeSlotProps = {
	timeSlot: {start: string, end: string};
	onClick: (timeSlot: {start: string, end: string}) => void;
	isSelected: boolean;
	slotId: string;
	isLoading?: boolean;
	animateSelected?: boolean;
	disableSkeleton?: boolean;
}


const DoctorAvailableTimeSlot: React.FC<DoctorAvailableTimeSlotProps> = ({ 
	timeSlot, 
	onClick, 
	isSelected, 
	slotId, 
	isLoading = false, 
	animateSelected = false,
	disableSkeleton = false
}) => {

	return (
		<Skeletonize
			isLoading={isLoading}
			disableSkeleton={disableSkeleton}
			className='h-[50px] lg:h-[61px] rounded-[16px] overflow-hidden'
			animation='syncWave pulse'
		>
			<li
				id={slotId}
				onClick={() => onClick(timeSlot)}
				className={`
					${isSelected ? 'bg-[#0A9281] text-white' : 'bg-transparent text-gray-600'}
					transition-[background-color, color] duration-[400ms] ease-in-out
					font-medium
					text-[15px] lg:text-[17px]
					font-roboto
					flex
					justify-between
					items-center
					py-[0.7em]
					px-4 lg:px-5
					cursor-pointer
					h-fit
					rounded-[16px]
					border-[1px]
					border-solid
					border-gray-300
					gap-x-1
					whitespace-nowrap
					animate-fade-in-up-10
					${isSelected && animateSelected ? 'animate-gentle-pulse' : ''}
				`}
			>
				<img 
					src={ClockIcon} 
					style={{ 
						filter: isSelected ? 'brightness(0) invert(1) saturate(0)' : 'brightness(0)',
						transition: 'all 500ms ease-in-out',
						opacity: 0.6
					}}
				/>
			 	{timeSlot.start && formatTimestamp(timeSlot.start, { use24Hour: true, showTime: true, showDate: false, showDay: false }) }
			</li>
		</Skeletonize>
	);
}